import { isValidPhoneNumber } from "react-phone-number-input";

export const MAX_EMPLOYMENT_DAYS = 365;
export const MAX_NAME_LENGTH = 50;
export const MAX_TEXT_FIELD_LENGTH = 255;

const NAME_REGEX = /^[\p{L}\p{M}]+(?:[- '][\p{L}\p{M}]+)*$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PERSONAL_IDENTITY_NUMBER_REGEX = /^(\d{12}|\d{8}-\d{4})$/;

type StaffInputTranslationKey =
  | "Staff.AddStaffPanelAgreementTooLong"
  | "Staff.AddStaffPanelEmailInvalid"
  | "Staff.AddStaffPanelEmailTooLong"
  | "Staff.AddStaffPanelEmployeeNumberInvalid"
  | "Staff.AddStaffPanelEmploymentDaysInvalid"
  | "Staff.AddStaffPanelFirstNameInvalid"
  | "Staff.AddStaffPanelFirstNameTooLong"
  | "Staff.AddStaffPanelLastNameInvalid"
  | "Staff.AddStaffPanelLastNameTooLong"
  | "Staff.AddStaffPanelPersonalIdentityNumberInvalid"
  | "Staff.AddStaffPanelPersonalIdentityNumberTooLong"
  | "Staff.AddStaffPanelPhoneNumberInvalid"
  | "Staff.AddStaffPanelRoleTooLong"
  | "Staff.AddStaffPanelWorkPercentageInvalid"
  | "Staff.AddStaffPanelWorkplaceTooLong";

export type StaffInputValidationError =
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "invalidFormat";
    }
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "outOfRange";
    }
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "tooLong";
    };

type StaffNameField = "firstName" | "lastName";

interface EmployeeNumberValidationParams {
  readonly isRequired: boolean;
  readonly value: string;
}

interface NameValidationErrorParams {
  readonly field: StaffNameField;
  readonly value: string;
}

interface OptionalTextValidationErrorParams {
  readonly translationKey:
    | "Staff.AddStaffPanelAgreementTooLong"
    | "Staff.AddStaffPanelRoleTooLong"
    | "Staff.AddStaffPanelWorkplaceTooLong";
  readonly value: string;
}

// These rules mirror backend limits for user-entered values. Keeping their
// validation and error classification here prevents create/update drift.
// Empty mandatory fields intentionally have no inline error; save guards still
// block submission until those fields contain valid values.

function toInvalidFormatError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "invalidFormat" };
}

function toOutOfRangeError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "outOfRange" };
}

function toTooLongError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "tooLong" };
}

/** Whether a field has been answered at all, before any rule about what it holds. */
export function hasValue(value: string | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

export function isValidName(name: string): boolean {
  const normalizedValue = name.trim();
  return (
    normalizedValue.length > 0 &&
    normalizedValue.length <= MAX_NAME_LENGTH &&
    NAME_REGEX.test(normalizedValue)
  );
}

export function isValidOptionalEmail(email: string): boolean {
  const normalizedValue = email.trim();
  return (
    normalizedValue.length === 0 ||
    (normalizedValue.length <= MAX_TEXT_FIELD_LENGTH &&
      EMAIL_REGEX.test(normalizedValue))
  );
}

export function isValidOptionalText(text: string): boolean {
  return text.trim().length <= MAX_TEXT_FIELD_LENGTH;
}

export function isValidOptionalPersonalIdentityNumber(
  personalIdentityNumber: string,
): boolean {
  const normalizedValue = personalIdentityNumber.trim();
  return (
    normalizedValue.length === 0 ||
    (normalizedValue.length <= MAX_TEXT_FIELD_LENGTH &&
      PERSONAL_IDENTITY_NUMBER_REGEX.test(normalizedValue))
  );
}

export function isValidRequiredText(text: string): boolean {
  const normalizedValue = text.trim();
  return (
    normalizedValue.length > 0 &&
    normalizedValue.length <= MAX_TEXT_FIELD_LENGTH
  );
}

export function isValidOptionalEmploymentDays(days: string): boolean {
  const normalizedValue = days.trim();
  if (normalizedValue.length === 0) return true;
  if (!/^\d+$/.test(normalizedValue)) return false;
  const parsedValue = Number(normalizedValue);
  return (
    Number.isSafeInteger(parsedValue) &&
    parsedValue >= 0 &&
    parsedValue <= MAX_EMPLOYMENT_DAYS
  );
}

export function hasValidEmployeeNumber({
  isRequired,
  value,
}: EmployeeNumberValidationParams): boolean {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return !isRequired;
  if (!/^\d+$/.test(normalizedValue)) return false;
  const parsedValue = Number(normalizedValue);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0;
}

export function isValidWorkPercentage(workPercentage: string): boolean {
  const normalizedValue = workPercentage.trim();
  const parsedValue = Number(normalizedValue);
  return (
    normalizedValue.length > 0 &&
    Number.isFinite(parsedValue) &&
    parsedValue >= 1 &&
    parsedValue <= 100
  );
}

export function getNameValidationError({
  field,
  value,
}: NameValidationErrorParams): StaffInputValidationError | null {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0 || isValidName(value)) return null;
  if (normalizedValue.length > MAX_NAME_LENGTH) {
    return toTooLongError(
      field === "firstName"
        ? "Staff.AddStaffPanelFirstNameTooLong"
        : "Staff.AddStaffPanelLastNameTooLong",
    );
  }
  return toInvalidFormatError(
    field === "firstName"
      ? "Staff.AddStaffPanelFirstNameInvalid"
      : "Staff.AddStaffPanelLastNameInvalid",
  );
}

export function getOptionalEmailValidationError(
  email: string,
): StaffInputValidationError | null {
  const normalizedValue = email.trim();
  if (normalizedValue.length === 0 || isValidOptionalEmail(email)) {
    return null;
  }
  return normalizedValue.length > MAX_TEXT_FIELD_LENGTH
    ? toTooLongError("Staff.AddStaffPanelEmailTooLong")
    : toInvalidFormatError("Staff.AddStaffPanelEmailInvalid");
}

export function getOptionalTextValidationError({
  translationKey,
  value,
}: OptionalTextValidationErrorParams): StaffInputValidationError | null {
  if (isValidOptionalText(value)) return null;
  return toTooLongError(translationKey);
}

export function getOptionalPersonalIdentityNumberValidationError(
  personalIdentityNumber: string,
): StaffInputValidationError | null {
  const normalizedValue = personalIdentityNumber.trim();
  if (normalizedValue.length === 0) return null;
  if (normalizedValue.length > MAX_TEXT_FIELD_LENGTH) {
    return toTooLongError("Staff.AddStaffPanelPersonalIdentityNumberTooLong");
  }
  return isValidOptionalPersonalIdentityNumber(personalIdentityNumber)
    ? null
    : toInvalidFormatError("Staff.AddStaffPanelPersonalIdentityNumberInvalid");
}

export function getEmployeeNumberValidationError(
  employeeNumber: string,
): StaffInputValidationError | null {
  if (
    employeeNumber.trim().length === 0 ||
    hasValidEmployeeNumber({ isRequired: false, value: employeeNumber })
  ) {
    return null;
  }
  return toInvalidFormatError("Staff.AddStaffPanelEmployeeNumberInvalid");
}

export function getEmploymentDaysValidationError(
  days: string,
): StaffInputValidationError | null {
  if (isValidOptionalEmploymentDays(days)) return null;
  return toOutOfRangeError("Staff.AddStaffPanelEmploymentDaysInvalid");
}

export function getPhoneValidationError(
  phoneNumber: string,
): StaffInputValidationError | null {
  if (phoneNumber.trim().length === 0 || isValidPhoneNumber(phoneNumber)) {
    return null;
  }
  return toInvalidFormatError("Staff.AddStaffPanelPhoneNumberInvalid");
}

export function getWorkPercentageValidationError(
  workPercentage: string,
): StaffInputValidationError | null {
  if (
    workPercentage.trim().length === 0 ||
    isValidWorkPercentage(workPercentage)
  ) {
    return null;
  }
  return toOutOfRangeError("Staff.AddStaffPanelWorkPercentageInvalid");
}
